<template>
 <div>
   <div class="desc">
     可配置规则为：
     <br/>
     1.域名: example.com
     <br/>
     2.通配符域名: *.example.com, *ple.com
     <br/>
     3.ip: 114.114.114.114
     <br/>
     4.网段：192.168.1.0/24
   </div>
   <div class="editor-container" style="padding: 0 16px" ref="containerRef">
     <div ref="editRef" style="width: 80%; height: 400px" />
   </div>
 </div>
</template>

<script lang="ts" setup>
import { defineProps, onMounted, ref, shallowRef, watch } from 'vue';
import { WidgetInfo } from '@/components/business/crud/widgets/WidgetContainer';
import { ScForm } from '@/models/ScForm';
import { JSONFieldType } from '@/components/business/crud/interface/FieldType';
import * as monaco from 'monaco-editor';
import { monacoHolder } from '@/util/monaco';

const props = defineProps<{
  propName: string;
  widget: JSONFieldType;
  info: WidgetInfo;
}>();

const data = ScForm.useFormData();
const containerRef = ref<HTMLElement>();
const editRef = ref<HTMLElement>();
const viewRef = ref<HTMLElement>();
const editor = shallowRef<monaco.editor.IStandaloneCodeEditor>();
const viewer = shallowRef<monaco.editor.IStandaloneCodeEditor>();

onMounted(() => {
  if (editRef.value) {
    const resizeObserver = new ResizeObserver(() => {
      if (editor.value) {
        editor.value.layout();
      }
    });
    resizeObserver.observe(editRef.value);
  }
  if (viewRef.value) {
    const resizeObserver = new ResizeObserver(() => {
      if (viewer.value) {
        viewer.value.layout();
      }
    });
    resizeObserver.observe(viewRef.value);
  }
});

watch([editRef], () => {
  if (editor.value) {
    editor.value.dispose();
  }
  if (!editRef.value) {
    return;
  }
  editor.value = monacoHolder.value.editor.create(editRef.value, {
    value: data.value[props.propName],
    language: 'plaintext',
    theme: 'vs-dark',
    minimap: {
      enabled: false,
    },
    readOnly: props.info.status === 'show',
  });
  check();
  editor.value.onDidChangeModelContent(check);
});

function check() {
  if (!editor.value) {
    return;
  }
  const model = editor.value.getModel();
  if (!model) {
    return;
  }
  const markers: monaco.editor.IMarkerData[] = [];
  const lines = model.getLinesContent();
  lines.forEach((lineContent, lineNumber) => {
    if (isInvalid(lineContent)) {
      markers.push({
        startLineNumber: lineNumber + 1,
        startColumn: 1,
        endLineNumber: lineNumber + 1,
        endColumn: lineContent.length + 1,
        message:
          '规则格式不正确\n必须是以下几种格式之一。ip地址如1.2.3.4, 网段如1.2.3.4/8, 域名如example.com或通配符域名如*.exa*.com ',
        severity: monaco.MarkerSeverity.Error,
      });
    }
  });
  monacoHolder.value.editor.setModelMarkers(
    model,
    'plaintext-validation',
    markers
  );
  data.value[props.propName] = editor.value.getValue();
}

const ipv4Reg =
  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/[0-9]{1,2})?$/;
const ipv6Reg = /^([0-9a-fA-F]{1,4}:){7}([0-9a-fA-F]{1,4})(\/[0-9]{1,3})?$/;
const hostReg = /^([a-zA-Z0-9*-]+\.)+[a-zA-Z*]+$/;

function isInvalid(content: string) {
  return (
    content !== '' &&
    !ipv4Reg.test(content) &&
    !ipv6Reg.test(content) &&
    !hostReg.test(content)
  );
}

watch(
  () => data.value[props.propName],
  (value) => {
    if (editor.value && editor.value.getValue() !== value) {
      editor.value.setValue(value);
    }
  }
);

watch(
  () => props.info.status,
  (value) => {
    if (editor.value) {
      editor.value.updateOptions({
        readOnly: value === 'show',
      });
    }
  }
);
</script>

<style lang="scss" scoped>
.title {
  font-size: $font-size;
  padding: 8px 8px;
  border: 1px solid #f0f0f0;
  border-bottom: none;
  display: flex;
  flex-direction: row;
  cursor: pointer;
}

.ant-descriptions-view .editor-container table {
  table-layout: unset;
  width: auto;
}
.desc {
  padding: 0 16px 16px;
}
</style>
